/**
 * FieldAgentStatusBadge — Iter-92 · Reward & Status pill.
 *
 * Auto-computed by `GET /api/service-summary/status/me`.
 * Colours:
 *   Green   → Active / On Route
 *   Amber   → Standby / Checked In
 *   Red     → Off Duty / Inactive
 *   Purple  → Top Performer this week (leaderboard-driven)
 *
 * The badge is READ-ONLY — agents cannot manually change their color.
 * Renders inline for header use, or full-width for the login card via
 * the `variant="card"` prop.
 */
import { useEffect, useState } from 'react';
import { Trophy, Radio, PauseCircle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PALETTE = {
  green:  { bg: 'rgba(16,185,129,0.15)',  fg: '#10b981', border: 'rgba(16,185,129,0.5)',  Icon: Radio },
  amber:  { bg: 'rgba(245,158,11,0.15)',  fg: '#f59e0b', border: 'rgba(245,158,11,0.5)',  Icon: PauseCircle },
  red:    { bg: 'rgba(239,68,68,0.15)',   fg: '#ef4444', border: 'rgba(239,68,68,0.5)',   Icon: XCircle },
  purple: { bg: 'rgba(168,85,247,0.18)',  fg: '#c084fc', border: 'rgba(168,85,247,0.6)',  Icon: Trophy },
};

export default function FieldAgentStatusBadge({ variant = 'inline' }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/service-summary/status/me`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setStatus(d);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!status) return null;
  const p = PALETTE[status.color] || PALETTE.red;
  const { Icon } = p;

  if (variant === 'card') {
    return (
      <div
        className="flex items-center gap-2 p-2 rounded-lg border-2"
        style={{ backgroundColor: p.bg, borderColor: p.border, color: p.fg }}
        data-testid="field-agent-status-card"
      >
        <Icon className="h-4 w-4 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest font-bold">Current Status</p>
          <p className="text-sm font-black" data-testid="field-agent-status-card-label">{status.label}</p>
        </div>
        {(status.streak_days || 0) >= 3 && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border shrink-0"
            style={{ backgroundColor: 'rgba(249,115,22,0.18)', borderColor: 'rgba(249,115,22,0.5)', color: '#fb923c' }}
            data-testid="field-agent-streak-chip-card"
          >
            🔥 {status.streak_days}-shift streak
          </span>
        )}
      </div>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5" data-testid="field-agent-status-wrapper">
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-widest font-bold"
        style={{ backgroundColor: p.bg, borderColor: p.border, color: p.fg }}
        title={status.label}
        data-testid="field-agent-status-badge"
      >
        <Icon className="h-3 w-3" />
        {status.color === 'purple' ? 'Top Performer' : status.color === 'green' ? 'On Route' : status.color === 'amber' ? 'Standby' : 'Off Duty'}
      </span>
      {/* Iter-93 · Shift streak chip — appears when ≥ 3 consecutive shifts. */}
      {(status.streak_days || 0) >= 3 && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border"
          style={{ backgroundColor: 'rgba(249,115,22,0.15)', borderColor: 'rgba(249,115,22,0.5)', color: '#fb923c' }}
          title={`${status.streak_days}-shift streak`}
          data-testid="field-agent-streak-chip"
        >
          🔥 {status.streak_days}-shift streak
        </span>
      )}
    </span>
  );
}
